import { useContext, useEffect, useState } from 'react';
import { cacheServiceContext } from '../services';
import { useGetValidatorAuctionMetrics } from './event-store-hooks';
import { motesToCSPR } from '../utils/currency';
import { useGetStatusMetrics } from './use-get-status-metrics';

export type Metrics = {
  blockHeight: number;
  blockHeightTimestamp: string;
  casperNetworkVersion: string;
  casperNetworkCommit: string;
  casperLiveVersion: string;
  casperLiveCommit: string;
  webAppVersion: string;
  allActiveBidsCount: number;
  activeValidatorsCount: number;
  eraTotalStake: string;
  circulatingSupply: string;
  totalSupply: string;
};

export type MetricsState = {
  webAppVersion: string;
  allActiveBidsCount: number;
  activeValidatorsCount: number;
  eraTotalStake: string;
  circulatingSupply: string;
  totalSupply: string;
};

export type GetMetricsType = {
  data: Metrics;
  loading: boolean;
};

export const useGetMetrics = (): GetMetricsType => {
  const [data, setData] = useState<null | MetricsState>(null);

  const { getGlobalState } = useContext(cacheServiceContext);
  const { data: supply } = getGlobalState('supply');

  const { data: validatorMetrics, loading: validatorMetricsLoading } =
    useGetValidatorAuctionMetrics({});

  useEffect(() => {
    let activeValidatorsCount = 0;
    let allActiveBidsCount = 0;
    let eraTotalValidatorWeight = '0';
    if (validatorMetrics?.data == null || supply == null) {
      return;
    }

    if (validatorMetrics?.data) {
      activeValidatorsCount = validatorMetrics?.data.active_validator_number;
      allActiveBidsCount = validatorMetrics?.data.active_bids_number;
      eraTotalValidatorWeight = validatorMetrics?.data.total_active_era_stake;
    }

    const { circulating: circulatingSupply, total: totalSupply } = supply || {};

    const metrics: MetricsState = {
      activeValidatorsCount,
      allActiveBidsCount,
      eraTotalStake: motesToCSPR(eraTotalValidatorWeight),
      webAppVersion: '0.0.0',
      circulatingSupply: circulatingSupply?.toString(),
      totalSupply: totalSupply?.toString(),
    };

    setData(metrics);
  }, [supply, validatorMetrics]);

  const { data: statusMetrics, loading: statusMetricsLoading } =
    useGetStatusMetrics();

  const loading =
    data == null ||
    validatorMetricsLoading ||
    supply == null ||
    statusMetricsLoading;

  const metrics = data && statusMetrics ? { ...data, ...statusMetrics } : null;

  return { data: metrics as Metrics, loading };
};
